import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import TopNav from "@/components/TopNav";
import { useAuth } from "@/auth/AuthContext";

function SessionRow({ s }) {
  const started = new Date(s.started_at).toLocaleString();
  const ended = s.ended_at ? new Date(s.ended_at).toLocaleString() : "—";

  const mapLink = `https://www.google.com/maps?q=${s.location.lat},${s.location.lng}`;

  const typeBadgeClass =
    s.activity_type === "loading"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div
      data-testid={`session-row-${s.session_id}`}
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Badge data-testid={`session-type-${s.session_id}`} className={typeBadgeClass} variant="outline">
              {s.activity_type}
            </Badge>
            <div className="text-sm text-slate-900" data-testid={`session-id-${s.session_id}`}>
              {s.session_id}
            </div>
          </div>
          <div className="text-xs text-slate-500" data-testid={`session-times-${s.session_id}`}>
            Start: {started} • End: {ended}
          </div>
          <div className="text-xs text-slate-600" data-testid={`session-counters-${s.session_id}`}>
            Cases: <span className="text-slate-900">{s.counters?.cases ?? 0}</span> • Sacks:{" "}
            <span className="text-slate-900">{s.counters?.sacks ?? 0}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            data-testid={`session-map-link-${s.session_id}`}
            className="text-sm text-sky-700 hover:text-sky-900"
            href={mapLink}
            target="_blank"
            rel="noreferrer"
          >
            Map
          </a>
          <Link
            to={`/sessions/${s.session_id}`}
            data-testid={`session-open-link-${s.session_id}`}
            className="text-sm text-slate-700 hover:text-slate-900"
          >
            Open
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [driveStatus, setDriveStatus] = useState(null);

  const loadSessions = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await api.get("/pod-sessions");
      setSessions(res.data.sessions || []);
    } catch (e) {
      setError("Could not load sessions.");
    } finally {
      setLoading(false);
    }
  };

  const loadDriveStatus = async () => {
    try {
      const res = await api.get("/drive/status");
      setDriveStatus(res.data);
    } catch (_) {
      setDriveStatus(null);
    }
  };

  useEffect(() => {
    loadSessions();
    loadDriveStatus();
  }, []);

  const stats = useMemo(() => {
    const total = sessions.length;
    const uploaded = sessions.filter((s) => s.drive_upload_status === "uploaded").length;
    const pending = sessions.filter((s) => s.drive_upload_status === "pending").length;
    const notConfigured = sessions.filter((s) => s.drive_upload_status === "not_configured").length;
    return { total, uploaded, pending, notConfigured };
  }, [sessions]);

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 py-8 space-y-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="dashboard-title">
              Dashboard
            </div>
            <div className="mt-2 text-base md:text-lg text-slate-600" data-testid="dashboard-subtitle">
              {user?.role === "admin" ? "Fleet-wide audit trail" : "Your capture sessions"}
            </div>
            <div className="mt-2 text-sm text-slate-700" data-testid="dashboard-drive-status-text">
              Drive: {driveStatus ? (driveStatus.configured ? "Configured" : "Not configured") : "Checking"}
            </div>
          </div>
          <Link to="/sessions/new" data-testid="dashboard-new-session-link">
            <Button
              className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
              data-testid="dashboard-new-session-button"
            >
              New capture
            </Button>
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card data-testid="stat-total-card" className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600" data-testid="stat-total-title">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold" data-testid="stat-total-value">{stats.total}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-uploaded-card" className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600" data-testid="stat-uploaded-title">Uploaded</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold" data-testid="stat-uploaded-value">{stats.uploaded}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-pending-card" className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600" data-testid="stat-pending-title">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold" data-testid="stat-pending-value">{stats.pending}</div>
            </CardContent>
          </Card>
        </div>

        <Card data-testid="sessions-list-card" className="border-slate-200 bg-white shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle data-testid="sessions-list-title">Recent sessions</CardTitle>
            <Button
              data-testid="sessions-refresh-button"
              variant="secondary"
              className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
              onClick={loadSessions}
            >
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {error ? (
              <div data-testid="sessions-load-error" className="text-sm text-rose-700">{error}</div>
            ) : null}
            {loading ? (
              <div data-testid="sessions-loading" className="text-sm text-slate-600">Loading…</div>
            ) : null}
            {!loading && sessions.length === 0 ? (
              <div data-testid="sessions-empty" className="text-sm text-slate-600">No sessions yet. Create your first capture.</div>
            ) : null}

            <div className="mt-4 space-y-3" data-testid="sessions-list">
              {sessions.map((s) => (
                <SessionRow key={s.session_id} s={s} />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
